// ---- Structure system ----
// Walkable architecture, parallel to the scatter `deco` system. Where a deco is
// a single visual prop reduced to one circle collider, a STRUCTURE is a building
// you move through: it emits visuals AND the physics that back them — walls you
// can't pass, floors you stand on, stairs you climb between them.
//
// A structure is authored in LOCAL coordinates (origin = its own footprint
// center, feet at y=0) via the helpers on StructureBuildCtx. `buildStructure`
// runs it, then translates the whole result (mesh + colliders + surfaces +
// lights + map dots) to wherever it's placed in the world. Add a new building by
// writing one file (see castle.ts) and placing it in structure/index.ts.
//
// Authoring helpers do double duty — `wall` draws the stone AND registers the
// box collider; `floorSlab` draws the slab AND registers the platform surface;
// `staircase` draws the steps AND registers the ramp. That keeps the visual and
// the physics from drifting apart. Raw `box`/`cone`/`cylinder` from the mesh kit
// are also exposed for pure decoration (battlements, banners, trim).

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { makePrims, UNIT_BOX, UNIT_SHARP, UNIT_CYL, UNIT_SPHERE, type Prims } from "../meshkit.ts";
import { makeToon, toonMat, TOON_SMOOTH, TOON_GRADIENT } from "../toon.ts";
import type { Collider, BoxCollider, CircleCollider, Surface } from "../physics.ts";

export type StructureBuildCtx = Prims & {
  /** Per-instance seed for deterministic variation. */
  seed: number;

  /** Solid wall: stone box from baseY up `height`, plus a matching box collider.
   *  Pass `{ collide: false }` for a non-blocking visual (e.g. a thin lintel).
   *  The wall TOP is standable by default (gravity plan — land on ledges/wall
   *  tops); pass `{ top: false }` to opt a run out. Wall tops are PROP
   *  platforms: never storey floors, so cutaway/shell-splitting ignore them. */
  wall: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    baseY: number, height: number, color: number,
    opts?: { collide?: boolean; top?: boolean },
  ) => void;

  /** Collider with no visual — for blocking a region (e.g. a parapet over a
   *  gateway that should only block the upper floor). */
  collideBox: (minX: number, maxX: number, minZ: number, maxZ: number, minY: number, maxY: number) => void;
  /** Circular collider with no visual. */
  collideCircle: (x: number, z: number, r: number, minY?: number, maxY?: number) => void;

  /** collideBox whose TOP is standable (gravity plan): the collider plus a
   *  prop platform at `maxY`. THE physics for furniture/prop masses a player
   *  can jump onto (tables, chests, crates, counters). OPTIONAL hook (older
   *  partial mock ctxs may omit it — call as `c.standBox?.(…)`). */
  standBox?: (minX: number, maxX: number, minZ: number, maxZ: number, minY: number, maxY: number) => void;
  /** collideCircle whose TOP is standable: collider + an inscribed-square prop
   *  platform at `maxY` (barrels, round tables). OPTIONAL hook. */
  standCircle?: (x: number, z: number, r: number, minY: number, maxY: number) => void;
  /** A standable SLOPE with no visual/collider — the roof-pitch surface
   *  (gravity plan): land on a roof and walk its pitch. OPTIONAL hook. */
  standRamp?: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    yLow: number, yHigh: number, axis: "x" | "z", lowAt: "min" | "max",
  ) => void;

  /** A walkable floor slab: a thin box whose TOP is at `y`, plus a platform
   *  surface so characters stand on it. The surface defaults to `y`, but pass
   *  `surfaceY` to register the walk height a little above the slab top — used
   *  when a proud paving veneer (flagstones) sits on the slab, so feet rest on
   *  the visible paving rather than sinking to the bare slab beneath it. */
  floorSlab: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    y: number, thickness: number, color: number, surfaceY?: number,
  ) => void;

  /** A NON-walkable ceiling slab capping an enclosed top floor (its top at `y`).
   *  Draws the slab and records `y` as the structure's `roofCeiling` so the
   *  cutaway cuts the top storey — without this, a roofed top floor stays opaque
   *  and hides the player (the castle dodges it with an open-air rampart). Use
   *  this instead of `floorSlab` for the lid of the topmost room: it adds NO
   *  walkable surface, so it doesn't register a phantom floor you could "stand"
   *  on. Like upper floorSlabs, keep its edges tucked inside the walls. */
  ceiling: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    y: number, thickness: number, color: number,
  ) => void;

  /** A staircase: visual stepped mass + a ramp surface from yLow→yHigh along
   *  `axis`. `lowAt` says which end of that axis sits at yLow. */
  staircase: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    yLow: number, yHigh: number,
    axis: "x" | "z", lowAt: "min" | "max",
    color: number, steps?: number,
  ) => void;

  /** Battlement merlons along the top edge of a wall run (decorative only). */
  crenellate: (
    minX: number, maxX: number, minZ: number, maxZ: number,
    topY: number, color: number, axis: "x" | "z",
  ) => void;

  /** A round tower: cylindrical shaft + conical roof + a full-height circle
   *  collider. Returns nothing; purely placed. */
  tower: (cx: number, cz: number, r: number, height: number, color: number, roofColor: number) => void;

  /** Tag everything drawn in `fn` as wall "shell" with an explicit outward
   *  normal (nx,nz). Use it to wrap a whole facade (cobble veneer + windows +
   *  trim) so the entire face — including thin frames the shape-inference would
   *  miss — fades together when it's between the player and the camera. */
  shell: (nx: number, nz: number, fn: () => void) => void;

  /** Like shell, but wall-fades when EITHER of two façades faces the camera.
   *  For corner posts / stones that sit on two faces and would otherwise need
   *  keep() (which never wall-fades). */
  shellEither: (
    a: { nx: number; nz: number },
    b: { nx: number; nz: number },
    fn: () => void,
  ) => void;

  /** Tag everything drawn in `fn` as exempt from WALL fading — for features that
   *  must read clearly from inside, like a doorway frame marking the exit. NOTE: this
   *  only exempts the wall-normal fade; kept geometry still fades as OVERHEAD when it
   *  belongs to a storey above the player. */
  keep: (fn: () => void) => void;

  /** Tag prims as floor deck (slab / planks). Same as overhead for wall-fade, but
   *  cutaway NEVER hides them for being above the player — so the floor above
   *  stays visible when you're looking into a lower room. */
  asFloor: (fn: () => void) => void;

  /** Sharp-edged solid (UNIT_SHARP), no collider — respects the current keep/shell
   *  tag. Use for continuous roof planes and other coplanar building masses that
   *  must not show RoundedBox notches at joins. */
  solid: (x: number, y: number, z: number, w: number, h: number, d: number, color: number) => void;

  /** Freeform mesh (owned BufferGeometry — consolidates as "others"). For curved
   *  crowns and other shapes no shared unit prim covers. `smooth` picks the
   *  soft toon ramp (organic curves); omit for the chunky cel ramp. Optional
   *  `map` (e.g. CanvasTexture for shop signs) — client-only textures; server
   *  builds may omit the mesh. Honors the current keep/shell/noShadow tag. */
  mesh: (geometry: THREE.BufferGeometry, color: number, opts?: {
    smooth?: boolean;
    map?: THREE.Texture;
    transparent?: boolean;
  }) => void;

  /** Tag everything drawn in `fn` as NON-shadow-casting — light passes through
   *  it. For translucent panes (window glass): the lamps cast shadows so walls
   *  occlude their light, and this is how a window lets it leak out. */
  noShadow: (fn: () => void) => void;

  /** Warm light source (lit at night, like cabins/wells). */
  light: (x: number, y: number, z: number) => void;
  /** Minimap dot. */
  dot: (x: number, z: number, color: number) => void;

  /** Declare a SEAT — a sittable point on a furniture piece (bench/stool/
   *  throne): `y` = the seat SURFACE height (local), `face` = the sitter's
   *  outward facing (ry radians, local; composes with placement `turns`).
   *  OPTIONAL hook (the tagOp rule — doc.test.ts builds a partial mock ctx);
   *  furniture calls `c.seat?.(…)`. Collected into BuiltStructure.seats →
   *  world.userData.seats → the server's per-map click-to-sit registry. */
  seat?: (x: number, y: number, z: number, face: number) => void;

  /** Declare an interactive DOOR (local coords). `doorway()` always registers
   *  one when this hook exists — closed colliders + client leaf Groups. OPTIONAL
   *  (partial mocks); collected into BuiltStructure.doors → world.userData.doors. */
  door?: (d: DoorEmit) => void;

  /** Studio provenance (OPTIONAL — doc.ts calls it `c.tagOp?.(i)`): meshes
   *  emitted after this call carry `userData.op = i`, so the Builder's editable
   *  (unconsolidated) preview can raycast a mesh back to its doc op. The game
   *  build consolidates the tagged meshes away — zero runtime effect. */
  tagOp?: (i: number | null) => void;

  /** Studio layer tag (OPTIONAL — doc.ts / curveGableRoof): meshes emitted
   *  inside `fn` carry `userData.layer = name` (base|mass|roof|fill|trim|
   *  furnish). Builder solo-toggles + inspect-structure --png color by it.
   *  Nested calls override; game consolidate discards tags. */
  layer?: (name: string, fn: () => void) => void;
};

export type StructureSpec = {
  type: string;
  /** Minimap color for the structure's footprint dots default. */
  mapColor: number;
  build: (ctx: StructureBuildCtx) => void;
  /** Dev-studio only (src/studio): optional per-frame animation for the model
   *  viewer, receiving the built+normalized group. Ignored by the game and the
   *  headless server. */
  studioAnimate?: (o: THREE.Object3D, dt: number, t: number) => void;
};

/** Per-mesh cutaway tag (set in world coords): vertical span, horizontal center,
 *  and an outward horizontal normal (0,0 = none). The cutaway manager reads this
 *  off `mesh.userData.cut` to decide when to fade the mesh out. */
export type CutTag = {
  y0: number; y1: number;       // vertical span (world Y)
  cx: number; cz: number;       // horizontal center (world)
  nx: number; nz: number;       // outward horizontal normal, or (0,0) if not wall-like
  /** Optional second outward normal (corner posts that belong to two façades).
   *  Wall-fades when EITHER normal faces the camera. */
  nx2?: number; nz2?: number;
  over: boolean;                // eligible to hide as "overhead" when above the player's floor
  /** Walkable floor slab / plank deck — never overhead-hidden so upper storeys
   *  still show their floor when cutaway is looking into the room below. */
  floorSlab?: boolean;
};

/** What the cutaway system needs to know to treat a building as an enterable
 *  interior: its footprint (from the walkable floor slabs) and the floor heights
 *  you can stand on. Computed automatically from the structure's surfaces. */
export type InteriorInfo = {
  minX: number; maxX: number; minZ: number; maxZ: number;
  floors: number[]; // standing heights, ascending (e.g. [0, 5])
  top: number;      // highest point of the structure
  /** If the TOP floor is enclosed (a `ceiling()` was emitted above it), the height
   *  of that ceiling. The cutaway uses it to cut the top storey too — without it,
   *  an enclosed top floor would leave the roof + walls opaque and hide the player.
   *  Undefined for open-topped structures (e.g. the castle's rampart). */
  roofCeiling?: number;
};

/** A sittable point in WORLD coords (after baseY/turns placement): players
 *  click-to-sit here (seated pose + snap + face). Ids are minted per-map at
 *  build time (world.ts) — transient, NEVER a persistence key. */
export type Seat = { x: number; y: number; z: number; face: number };

/**
 * Interactive door emitted during structure build (LOCAL coords until place).
 * `doorway()` fills this; buildStructure transforms to world like seats.
 */
export type DoorEmit = {
  /** Hinge foot (local). */
  hx: number; hy: number; hz: number;
  /** Opening center (local) — proximity pick. */
  cx: number; cy: number; cz: number;
  w: number; h: number;
  /** THREE rotation.y when fully closed (leaf mesh extends +x from hinge). */
  closedRy: number;
  /** Added to closedRy when fully open (outward swing, ±π/2). */
  openDelta: number;
  /** Closed-state box collider (local AABB). */
  minX: number; maxX: number; minZ: number; maxZ: number;
  minY: number; maxY: number;
  /** Leaf wood color (toon hex). */
  color: number;
};

/**
 * Interactive door in WORLD coords after placement. Ids minted in world.ts
 * (`dr:<placement>:<i>`) — build-order transient, NEVER a persistence key
 * (same rule as seats). Default closed on boot; open state is session-only.
 */
export type StructureDoor = {
  hx: number; hy: number; hz: number;
  x: number; y: number; z: number;
  w: number; h: number;
  closedRy: number;
  openDelta: number;
  minX: number; maxX: number; minZ: number; maxZ: number;
  minY: number; maxY: number;
  color: number;
};

export type BuiltStructure = {
  group: THREE.Group;
  colliders: Collider[];
  surfaces: Surface[];
  lights: { x: number; y: number; z: number }[];
  dots: { x: number; z: number; color: number }[];
  /** Sittable furniture points, in world coords (see Seat). */
  seats: Seat[];
  /** Interactive doorways in world coords (see StructureDoor). */
  doors: StructureDoor[];
  /** Footprint + floor model for the cutaway system (empty floors = not enterable). */
  interior: InteriorInfo;
  /** Meshes that can fade for cutaway, each tagged on `userData.cut`. */
  cutaway: THREE.Mesh[];
};

// How "wall-like" a box must be to earn an outward normal (and thus camera-cull):
// its thinnest horizontal dimension must be under this fraction of its longest.
// Perimeter walls (1.4 thick × ~27 long) and veneer cobbles (0.14 thick) pass;
// square towers and floor slabs don't.
const PANEL_RATIO = 0.45;

/** Compute a cutaway tag from a freshly-placed mesh's local geometry. `override`
 *  forces intent: "shell" = a wall (gets an outward normal, so it fades when it's
 *  between camera and player), "over" / "skip" = no normal so never wall-faded —
 *  but BOTH still fade as "overhead" when they sit on a storey above the player
 *  (so upper-floor furniture/columns hide when you're below them). "floor" = like
 *  over but never overhead-hidden (walkable decks stay when looking from below).
 *  null = infer from shape (raw decoration like cobble veneer / windows). */
function classifyCut(
  mesh: THREE.Mesh,
  override: "shell" | "over" | "skip" | "floor" | null,
): CutTag | null {
  mesh.updateMatrix();
  if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
  const bb = mesh.geometry.boundingBox!.clone().applyMatrix4(mesh.matrix); // local-space AABB
  const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
  const dx = bb.max.x - bb.min.x, dz = bb.max.z - bb.min.z;
  const panel = Math.min(dx, dz) < PANEL_RATIO * Math.max(dx, dz);
  let nx = 0, nz = 0;
  if ((override === "shell" || override === null) && panel) {
    // Outward normal points along the thin horizontal axis, away from center.
    if (dx < dz) nx = cx > 0 ? 1 : cx < 0 ? -1 : 0;
    else nz = cz > 0 ? 1 : cz < 0 ? -1 : 0;
  }
  return {
    y0: bb.min.y, y1: bb.max.y, cx, cz, nx, nz, over: true,
    floorSlab: override === "floor" ? true : undefined,
  };
}

export function defineStructure(spec: StructureSpec): StructureSpec {
  return spec;
}

// ---- Per-storey shell split ----------------------------------------------------
// Mass walls / timber posts are often ONE continuous box from foundation to
// eaves. Cutaway can only fade whole meshes — so upstairs the entire façade
// vanished, including the ground-floor wall the player should still see around
// the floor below. Split wall-like unit boxes at every intermediate floor line
// BEFORE consolidation so each storey is its own fade class.
//
// AUTHORING CONSTRAINT: only UNIT_SHARP / UNIT_BOX (axis-aligned scale-as-size
// prims) are split. Freeform / rotated / unique-geo façades stay ONE mesh and
// will still full-height-fade upstairs — emit continuous multi-storey shells
// via wall()/box()/solid (UNIT_SHARP) or timber posts (UNIT_BOX), not freeform.
// See structure/CLAUDE.md "Multi-storey cutaway".
const SPLIT_MARGIN = 0.25; // ignore floors within this of a mesh top/bottom
// Hard abut at each floor line (no gap = no black void, no overlap = wall
// solids don't pierce the floor slab and z-fight). Floor decks use
// polygonOffset so their top face doesn't shimmer against the wall join.

function splitShellsByFloors(
  group: THREE.Group,
  cutaway: THREE.Mesh[],
  interior: InteriorInfo,
  /** When true (game/server), consolidate will replace materials — shared
   *  toonMat refs on clones are fine. When false (studio Builder), each
   *  segment needs its own material so cutaway opacity can't clobber peers. */
  privateMats: boolean,
): void {
  // Split planes = every standing floor above the ground (and not the roof
  // ceiling — that caps the top storey, it doesn't divide two wall bands).
  const planes = interior.floors.slice(1);
  if (planes.length === 0) return;

  const next: THREE.Mesh[] = [];
  for (const mesh of cutaway) {
    const cut = mesh.userData.cut as CutTag;
    const isWall = cut.nx !== 0 || cut.nz !== 0 ||
      cut.nx2 !== undefined || cut.nz2 !== undefined;
    const isUnitBox =
      mesh.geometry === UNIT_SHARP || mesh.geometry === UNIT_BOX;
    if (!isWall || !isUnitBox) {
      next.push(mesh);
      continue;
    }

    // Floors strictly inside this mesh's vertical span.
    const hits = planes.filter(
      (y) => y > cut.y0 + SPLIT_MARGIN && y < cut.y1 - SPLIT_MARGIN,
    );
    if (hits.length === 0) {
      next.push(mesh);
      continue;
    }

    // World Y ↔ local Y: group lifts by baseY; rotation is about Y so y is free.
    const localBottom = mesh.position.y - mesh.scale.y / 2;
    const worldToLocal = (wy: number) => localBottom + (wy - cut.y0);

    const bands: [number, number][] = [];
    let lo = cut.y0;
    for (const p of hits) {
      bands.push([lo, p]);
      lo = p;
    }
    bands.push([lo, cut.y1]);

    group.remove(mesh);
    for (const [b0, b1] of bands) {
      const h = b1 - b0;
      if (h < 0.12) continue;
      const seg = mesh.clone();
      // Unit geos stay shared (never dispose). Materials: game consolidate
      // rebuilds private fade mats; studio (privateMats) clones so cutaway
      // opacity is per-band (Material.clone drops onBeforeCompile rim — floors
      // of a studio preview rarely need rim; walls use makeToon when possible).
      seg.position.y = (worldToLocal(b0) + worldToLocal(b1)) / 2;
      seg.scale.y = h;
      if (privateMats) {
        const src = mesh.material as THREE.MeshToonMaterial;
        // Fresh makeToon so rim/onBeforeCompile survive (Material.clone drops them).
        const col = src.color?.getHex?.() ?? 0xffffff;
        const m = makeToon({
          color: col,
          transparent: src.transparent,
          opacity: src.opacity,
          side: src.side,
        });
        if (src.userData?.smooth || src.gradientMap === TOON_SMOOTH) {
          m.gradientMap = TOON_SMOOTH;
          m.userData.smooth = true;
        }
        m.userData.owned = true;
        seg.material = m;
      }
      // Preserve provenance / noShadow / layer tags for Builder + consolidate.
      seg.userData = { ...mesh.userData };
      seg.userData.cut = {
        ...cut,
        y0: b0,
        y1: b1,
        // Horizontal center unchanged (cx/cz).
      } satisfies CutTag;
      group.add(seg);
      next.push(seg);
    }
  }
  cutaway.length = 0;
  for (const m of next) cutaway.push(m);
}

// ---- Consolidation pass (knowledge-base/plans/02 Phase C) ---------------------------------
// spec.build() emits one Mesh per primitive — a castle is ~3k meshes, which
// used to mean ~3k draw calls, ~3k scene-graph objects, and a cloned material
// each so cutaway could fade them individually. But updateCutaway's fade
// decision reads ONLY the tag's outward normal (nx,nz) and how (y0,y1) sit
// against the structure's floor/ceiling lines — never the mesh identity — so
// every mesh in the same "fade-equivalence class" always fades in lockstep and
// can be drawn as ONE object with ONE private material, with zero behavior
// change.
//
// Shared-geo prims (UNIT_SHARP walls/floors, UNIT_BOX accents, UNIT_CYL /
// UNIT_SPHERE soft trim) each become one InstancedMesh per cutaway class.
// Unique geos (tapered cylinders, cones, bones, lathes, blobs) merge into one
// vertex-colored mesh per class. A castle collapses to a few dozen objects.
//
// Wall shells are storey-split first (splitShellsByFloors) so upstairs cutaway
// can melt only THIS floor's façade while the walls around the floor below stay.
function consolidate(group: THREE.Group, cutaway: THREE.Mesh[], interior: InteriorInfo): void {
  // The exact thresholds updateCutaway compares a tag against (cutaway.ts):
  //   overhead: y0 >= ceiling - 0.5   (ceiling ∈ upper floors ∪ roofCeiling)
  //   wall:     y1 > floor + 0.3 && y0 < ceiling   (floor ∈ floors — THIS storey only)
  // Two tags on the same side of every threshold behave identically forever.
  const ceilings = interior.floors.slice(1);
  if (interior.roofCeiling !== undefined) ceilings.push(interior.roofCeiling);
  const y0Cuts = ceilings.flatMap((c) => [c - 0.5, c]).sort((a, b) => a - b);
  // Match cutaway's "extends into my storey air" threshold (floor + 0.3).
  const y1Cuts = interior.floors.map((f) => f + 0.3).sort((a, b) => a - b);
  const bucket = (v: number, cuts: number[]) => {
    let n = 0;
    for (const c of cuts) if (v >= c) n++;
    return n;
  };

  type Cls = {
    boxes: THREE.Mesh[];
    sharps: THREE.Mesh[];
    cyls: THREE.Mesh[];
    spheres: THREE.Mesh[];
    others: THREE.Mesh[];
    tag: CutTag;
    n: number;
    sx: number;
    sz: number;
    noShadow: boolean;
  };
  const classes = new Map<string, Cls>();
  const passthrough: THREE.Object3D[] = [];
  for (const child of group.children) {
    const mesh = child as THREE.Mesh;
    const cut = mesh.userData?.cut as CutTag | undefined;
    if (!(mesh as unknown as { isMesh?: boolean }).isMesh || !cut) {
      passthrough.push(child); // defensive — push() only ever adds tagged meshes
      continue;
    }
    // Textured freeform (shop-sign CanvasTexture, etc.): baking into vertex-
    // colored "others" strips the map and disposes the material → blank board.
    // Keep as passthrough; re-register on cutaway AFTER the wipe below.
    const matMap = (mesh.material as THREE.MeshBasicMaterial | THREE.MeshToonMaterial)?.map;
    if (matMap || mesh.userData.noConsolidate) {
      passthrough.push(mesh);
      continue;
    }
    // noShadow (window glass) splits the class: it must land in its own merged
    // mesh so castShadow can differ from the wall it otherwise fades with.
    const noShadow = mesh.userData.noShadow === true;
    const key = `${cut.nx},${cut.nz},${cut.nx2 ?? ""},${cut.nz2 ?? ""},${bucket(cut.y0, y0Cuts)},${bucket(cut.y1, y1Cuts)},${noShadow ? 1 : 0},${cut.floorSlab ? 1 : 0}`;
    let cls = classes.get(key);
    if (!cls) {
      classes.set(key, (cls = {
        boxes: [], sharps: [], cyls: [], spheres: [], others: [],
        tag: { ...cut }, n: 0, sx: 0, sz: 0, noShadow,
      }));
    } else {
      cls.tag.y0 = Math.min(cls.tag.y0, cut.y0);
      cls.tag.y1 = Math.max(cls.tag.y1, cut.y1);
    }
    cls.n++; cls.sx += cut.cx; cls.sz += cut.cz;
    if (mesh.geometry === UNIT_BOX) cls.boxes.push(mesh);
    else if (mesh.geometry === UNIT_SHARP) cls.sharps.push(mesh);
    else if (mesh.geometry === UNIT_CYL) cls.cyls.push(mesh);
    else if (mesh.geometry === UNIT_SPHERE) cls.spheres.push(mesh);
    else cls.others.push(mesh);
  }

  group.clear();
  for (const o of passthrough) group.add(o);
  cutaway.length = 0;
  // Re-register textured passthrough meshes for overhead cutaway (they still
  // carry userData.cut from push(); skip wall-fade since nx=nz=0 under keep()).
  for (const o of passthrough) {
    const m = o as THREE.Mesh;
    if ((m as unknown as { isMesh?: boolean }).isMesh && m.userData?.cut) cutaway.push(m);
  }

  const adopt = (mesh: THREE.Mesh, cls: Cls) => {
    mesh.castShadow = !cls.noShadow;
    mesh.receiveShadow = false; // interiors stay evenly lit (see push())
    mesh.userData.cut = { ...cls.tag, cx: cls.sx / cls.n, cz: cls.sz / cls.n };
    // Private per-build material (cutaway mutates opacity) — a map swap
    // disposes it (meshkit disposeGroup); the toonMat cache is never tagged.
    const mat = mesh.material as THREE.Material;
    mat.userData.owned = true;
    // Floor decks share an edge with storey-split walls at the slab y — without
    // a depth bias they shimmer (stair-step z-fight). Pull floors slightly
    // forward in the depth buffer so the deck wins cleanly.
    if (cls.tag.floorSlab) {
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = -1;
      mat.polygonOffsetUnits = -2;
    }
    group.add(mesh);
    cutaway.push(mesh);
  };

  const adoptInstances = (geo: THREE.BufferGeometry, meshes: THREE.Mesh[], cls: Cls) => {
    if (!meshes.length) return;
    // Private material (NOT the toonMat cache): cutaway mutates its opacity.
    // Base color stays white so per-instance colors render exactly.
    const im = new THREE.InstancedMesh(geo, makeToon(), meshes.length);
    for (let i = 0; i < meshes.length; i++) {
      const m = meshes[i]!;
      m.updateMatrix();
      im.setMatrixAt(i, m.matrix);
      im.setColorAt(i, (m.material as THREE.MeshToonMaterial).color);
    }
    adopt(im, cls);
  };

  for (const cls of classes.values()) {
    adoptInstances(UNIT_BOX, cls.boxes, cls);
    adoptInstances(UNIT_SHARP, cls.sharps, cls);
    adoptInstances(UNIT_CYL, cls.cyls, cls);
    adoptInstances(UNIT_SPHERE, cls.spheres, cls);
    if (cls.others.length) {
      // Split smooth (curve-roof freeform) vs chunky unique geos so we don't
      // force TOON_SMOOTH onto bone/cone "others" (or vice versa).
      const smoothOthers = cls.others.filter(
        (m) => (m.material as THREE.Material).userData?.smooth === true
          || (m.material as THREE.MeshToonMaterial).gradientMap === TOON_SMOOTH,
      );
      const chunkyOthers = cls.others.filter((m) => !smoothOthers.includes(m));

      const bakeOthers = (meshes: THREE.Mesh[], smooth: boolean) => {
        if (!meshes.length) return;
        // Capture material flags BEFORE dispose — owned mats are freed below.
        const anyDouble = meshes.some(
          (m) => (m.material as THREE.Material).side === THREE.DoubleSide,
        );
        const parts = meshes.map((m) => {
          m.updateMatrix();
          // Unique geos (tapered cyls, cones, bones, freeform roofs…): clone +
          // bake transform, keep position+normal, add a flat vertex color.
          const g = (m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone());
          g.applyMatrix4(m.matrix);
          for (const name of Object.keys(g.attributes)) {
            if (name !== "position" && name !== "normal") g.deleteAttribute(name);
          }
          const col = (m.material as THREE.MeshToonMaterial).color;
          const n = g.attributes.position.count;
          const arr = new Float32Array(n * 3);
          for (let i = 0; i < n; i++) {
            arr[i * 3] = col.r; arr[i * 3 + 1] = col.g; arr[i * 3 + 2] = col.b;
          }
          g.setAttribute("color", new THREE.BufferAttribute(arr, 3));
          // Dispose per-build geos (never SHARED_GEOS in `others`).
          m.geometry.dispose();
          // Freeform mesh() materials are owned — dispose or they leak every
          // game/server build (map swap / structure rebuild).
          const mat0 = m.material as THREE.Material;
          if (mat0?.userData?.owned) mat0.dispose();
          return g;
        });
        const merged = mergeGeometries(parts, false)!;
        parts.forEach((p) => p.dispose());
        const mat = makeToon({
          vertexColors: true,
          side: anyDouble ? THREE.DoubleSide : THREE.FrontSide,
        });
        // makeToon stamps TOON_GRADIENT — restore soft ramp for curve roofs.
        if (smooth) mat.gradientMap = TOON_SMOOTH;
        else mat.gradientMap = TOON_GRADIENT;
        adopt(new THREE.Mesh(merged, mat), cls);
      };

      bakeOthers(chunkyOthers, false);
      bakeOthers(smoothOthers, true);
    }
  }
}

// --- quarter-turn placement rotation -----------------------------------------
// Rotation is restricted to 0/90/180/270° (`turns` = quarter-turns) so that
// axis-aligned box colliders/surfaces map to axis-aligned ones (an arbitrary
// angle would need oriented bounding boxes the physics layer doesn't support).
// The point transform matches THREE's `group.rotation.y = turns * π/2`:
//   rotation about +Y by θ → x' = x·cosθ + z·sinθ,  z' = -x·sinθ + z·cosθ.
function rotXZ(x: number, z: number, turns: number): [number, number] {
  switch (((turns % 4) + 4) % 4) {
    case 1: return [z, -x];
    case 2: return [-x, -z];
    case 3: return [-z, x];
    default: return [x, z];
  }
}
// Rotate an axis-aligned XZ rectangle and return the new (still axis-aligned) box.
function rotRect(minX: number, maxX: number, minZ: number, maxZ: number, turns: number) {
  const [ax, az] = rotXZ(minX, minZ, turns);
  const [bx, bz] = rotXZ(maxX, maxZ, turns);
  return { minX: Math.min(ax, bx), maxX: Math.max(ax, bx), minZ: Math.min(az, bz), maxZ: Math.max(az, bz) };
}

/**
 * Run a structure's build() and return its mesh + physics, placed at
 * (originX, originZ) in the world. `baseY` lifts the whole structure (feet from
 * local y=0 to world `baseY`) so it can sit on terrain instead of always at 0.
 * `turns` rotates it by quarter-turns about Y (0–3). Round towers use circle
 * colliders (rotation-invariant) and walls stay axis-aligned under quarter turns.
 */
export function buildStructure(
  spec: StructureSpec, originX = 0, originZ = 0, baseY = 0, turns = 0,
  opts?: {
    /** false = SKIP the consolidation pass, keeping one mesh per primitive with
     *  its `userData.op` provenance tag — the studio Builder's editable preview
     *  (pickable, but many draw calls). The game/server always consolidate. */
    consolidate?: boolean;
  },
): BuiltStructure {
  const group = new THREE.Group();
  const colliders: Collider[] = [];
  const surfaces: Surface[] = [];
  const lights: { x: number; y: number; z: number }[] = [];
  const dots: { x: number; z: number; color: number }[] = [];
  const seats: Seat[] = [];
  const doors: StructureDoor[] = [];

  const cutaway: THREE.Mesh[] = [];
  let ceilingY: number | undefined; // top-floor ceiling height (see ceiling())
  // Helpers set this around their prim calls to declare cutaway intent; raw prim
  // calls (cobble veneer, windows) leave it null so the shape is inferred.
  let tagAs: "shell" | "over" | "skip" | "floor" | null = null;
  // When set (via `shell`), forces the outward normal instead of inferring it —
  // so thin window frames fade with the wall face they belong to.
  let forceNormal: { nx: number; nz: number } | null = null;
  // Optional second normal (via `shellEither`) — corner pieces fade if either
  // façade faces the camera.
  let forceNormal2: { nx: number; nz: number } | null = null;
  // When set (via `noShadow`), marks meshes as letting light through — kept out
  // of the shadow maps by the consolidation pass (window glass).
  let noShadowOn = false;

  // Current op index while a doc interpreter drives the build (see tagOp).
  let curOp: number | null = null;
  // Current architecture layer for Builder solo-toggles / inspect PNG (see layer).
  let curLayer: string | null = null;

  const push = (mesh: THREE.Mesh) => {
    mesh.castShadow = !noShadowOn;
    if (noShadowOn) mesh.userData.noShadow = true;
    if (curOp !== null) mesh.userData.op = curOp;
    if (curLayer !== null) mesh.userData.layer = curLayer;
    // Structures never RECEIVE the sun's cast shadows: this keeps the interior
    // evenly lit ("indoor" lighting) instead of having exterior walls stripe
    // shadows across the floor and inner walls. They still cast outward onto the
    // terrain, so the building stays grounded. (Self-shadowing on the exterior is
    // traded away for a consistent interior — the toon shading carries the form.)
    mesh.receiveShadow = false;
    group.add(mesh);
    const cut = classifyCut(mesh, tagAs);
    if (cut) {
      if (forceNormal && tagAs === "shell") {
        cut.nx = forceNormal.nx;
        cut.nz = forceNormal.nz;
        if (forceNormal2) {
          cut.nx2 = forceNormal2.nx;
          cut.nz2 = forceNormal2.nz;
        }
      }
      // No material clone here: these per-primitive meshes are transient — the
      // consolidation pass below replaces them with one object per fade class,
      // and THOSE get the private (fade-able) materials.
      mesh.userData.cut = cut;
      cutaway.push(mesh);
    }
  };
  // Run `fn` with a cutaway intent in effect, restoring the prior one after — so
  // the helpers are composable (e.g. `keep(() => wall(...))` isn't clobbered).
  const tagged = (t: typeof tagAs, fn: () => void) => {
    const prev = tagAs;
    tagAs = t;
    fn();
    tagAs = prev;
  };
  const prims = makePrims(push, spec.type.length + 7);
  const { box, cone, cylinder } = prims;

  /** Sharp-edged box for continuous building shells (walls/floors/ceilings).
   *  Uses UNIT_SHARP so coplanar flanks+lintels join without RoundedBox notches. */
  const sharpBox = (x: number, y: number, z: number, w: number, h: number, d: number, color: number) => {
    const m = new THREE.Mesh(UNIT_SHARP, toonMat(color));
    m.position.set(x, y, z);
    m.scale.set(w, h, d);
    push(m);
  };

  const ctx: StructureBuildCtx = {
    seed: spec.type.length + 7,
    ...prims,

    wall: (minX, maxX, minZ, maxZ, baseY, height, color, opts) => {
      // Sharp shell — THE continuous building face. Rounded UNIT_BOX walls were
      // carving soft valleys between every flank/lintel join (= "separate walls").
      tagged("shell", () =>
        sharpBox((minX + maxX) / 2, baseY + height / 2, (minZ + maxZ) / 2, maxX - minX, height, maxZ - minZ, color));
      if (opts?.collide !== false) {
        colliders.push({ kind: "box", minX, maxX, minZ, maxZ, minY: baseY, maxY: baseY + height });
        // Wall tops are landable (gravity plan): a PROP platform — storey
        // derivation / cutaway / shell-splitting ignore it (see Platform.prop).
        if (opts?.top !== false) {
          surfaces.push({ kind: "platform", minX, maxX, minZ, maxZ, y: baseY + height, prop: true });
        }
      }
    },

    collideBox: (minX, maxX, minZ, maxZ, minY, maxY) => {
      colliders.push({ kind: "box", minX, maxX, minZ, maxZ, minY, maxY });
    },
    collideCircle: (x, z, r, minY, maxY) => {
      colliders.push({ x, z, r, minY, maxY });
    },
    standBox: (minX, maxX, minZ, maxZ, minY, maxY) => {
      colliders.push({ kind: "box", minX, maxX, minZ, maxZ, minY, maxY });
      surfaces.push({ kind: "platform", minX, maxX, minZ, maxZ, y: maxY, prop: true });
    },
    standCircle: (x, z, r, minY, maxY) => {
      colliders.push({ x, z, r, minY, maxY });
      const h = r * 0.707; // inscribed square — feet stay over the round mass
      surfaces.push({ kind: "platform", minX: x - h, maxX: x + h, minZ: z - h, maxZ: z + h, y: maxY, prop: true });
    },
    standRamp: (minX, maxX, minZ, maxZ, yLow, yHigh, axis, lowAt) => {
      surfaces.push({ kind: "ramp", minX, maxX, minZ, maxZ, yLow, yHigh, axis, lowAt });
    },

    floorSlab: (minX, maxX, minZ, maxZ, y, thickness, color, surfaceY) => {
      tagged("floor", () =>
        sharpBox((minX + maxX) / 2, y - thickness / 2, (minZ + maxZ) / 2, maxX - minX, thickness, maxZ - minZ, color));
      surfaces.push({ kind: "platform", minX, maxX, minZ, maxZ, y: surfaceY ?? y });
    },

    ceiling: (minX, maxX, minZ, maxZ, y, thickness, color) => {
      tagged("over", () =>
        sharpBox((minX + maxX) / 2, y - thickness / 2, (minZ + maxZ) / 2, maxX - minX, thickness, maxZ - minZ, color));
      ceilingY = Math.max(ceilingY ?? -Infinity, y); // marks the top floor as enclosed
    },

    staircase: (minX, maxX, minZ, maxZ, yLow, yHigh, axis, lowAt, color, steps = 14) => {
      surfaces.push({ kind: "ramp", minX, maxX, minZ, maxZ, yLow, yHigh, axis, lowAt });
      const along = axis === "x" ? maxX - minX : maxZ - minZ;
      const rise = yHigh - yLow;
      tagged("skip", () => { // stairs are interior — never cut away
        for (let i = 0; i < steps; i++) {
          const f0 = i / steps, f1 = (i + 1) / steps;
          // Set the step's top to the DOWNHILL edge of its slice so the solid step
          // sits at or below the smooth walk-ramp across the whole slice — never
          // poking above it (which made the player clip through the stairs).
          const top = lowAt === "min" ? yLow + f0 * rise : yLow + (1 - f1) * rise;
          let sMinX = minX, sMaxX = maxX, sMinZ = minZ, sMaxZ = maxZ;
          if (axis === "x") { sMinX = minX + f0 * along; sMaxX = minX + f1 * along; }
          else { sMinZ = minZ + f0 * along; sMaxZ = minZ + f1 * along; }
          // Each step is a solid block from the flight's starting floor (yLow) up to
          // its tread — NOT from y=0. A flight that starts on an upper floor (yLow>0)
          // must sit on that floor, not fill the column down to the ground (which
          // also left its base at 0 so the cutaway never hid it from below).
          const stepH = Math.max(0.12, top - yLow);
          box((sMinX + sMaxX) / 2, (yLow + top) / 2, (sMinZ + sMaxZ) / 2,
            sMaxX - sMinX, stepH, sMaxZ - sMinZ, color);

          // Side walls (invisible): block walking into the tall side of the stairs
          // and clipping inside the steps. They run along the climb axis on both
          // edges and follow the step profile in height (so they only block where
          // the steps actually rise), leaving the low/high ends open to enter/exit.
          // Use the UPHILL edge height so the wall never sits below the walk ramp.
          const slabTop = lowAt === "min" ? yLow + f1 * rise : yLow + (1 - f0) * rise;
          if (slabTop > yLow + 1e-6) {
            if (axis === "x") {
              colliders.push({ kind: "box", minX: sMinX, maxX: sMaxX, minZ, maxZ: minZ, minY: yLow, maxY: slabTop });
              colliders.push({ kind: "box", minX: sMinX, maxX: sMaxX, minZ: maxZ, maxZ, minY: yLow, maxY: slabTop });
            } else {
              colliders.push({ kind: "box", minX, maxX: minX, minZ: sMinZ, maxZ: sMaxZ, minY: yLow, maxY: slabTop });
              colliders.push({ kind: "box", minX: maxX, maxX, minZ: sMinZ, maxZ: sMaxZ, minY: yLow, maxY: slabTop });
            }
          }
        }
      });
    },

    crenellate: (minX, maxX, minZ, maxZ, topY, color, axis) => {
      const w = maxX - minX, d = maxZ - minZ;
      const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
      const mh = 0.7; // merlon height
      tagged("over", () => { // battlements sit atop the walls — overhead from inside
        if (axis === "x") {
          const n = Math.max(2, Math.floor(w / 1.6));
          for (let i = 0; i < n; i++) {
            const x = minX + (i + 0.5) * (w / n);
            box(x, topY + mh / 2, cz, (w / n) * 0.55, mh, d, color);
          }
        } else {
          const n = Math.max(2, Math.floor(d / 1.6));
          for (let i = 0; i < n; i++) {
            const z = minZ + (i + 0.5) * (d / n);
            box(cx, topY + mh / 2, z, w, mh, (d / n) * 0.55, color);
          }
        }
      });
    },

    tower: (cx, cz, r, height, color, roofColor) => {
      tagged("skip", () => { // corner towers stay — they frame the view, rarely occlude
        cylinder(cx, 0, cz, r, r * 1.08, height, color);
        // a ring of corbels near the top, then the conical roof
        cylinder(cx, height - 0.4, cz, r * 1.18, r * 1.18, 0.5, color);
        cone(cx, height + 0.1, cz, r * 1.25, r * 1.7, roofColor);
      });
      colliders.push({ x: cx, z: cz, r, minY: 0, maxY: height });
    },

    shell: (nx, nz, fn) => {
      const pn = forceNormal;
      const pn2 = forceNormal2;
      forceNormal = { nx, nz };
      forceNormal2 = null;
      tagged("shell", fn);
      forceNormal = pn;
      forceNormal2 = pn2;
    },
    shellEither: (a, b, fn) => {
      const pn = forceNormal;
      const pn2 = forceNormal2;
      forceNormal = { nx: a.nx, nz: a.nz };
      forceNormal2 = { nx: b.nx, nz: b.nz };
      tagged("shell", fn);
      forceNormal = pn;
      forceNormal2 = pn2;
    },
    keep: (fn) => tagged("skip", fn),
    asFloor: (fn) => tagged("floor", fn),

    solid: (x, y, z, w, h, d, color) => sharpBox(x, y, z, w, h, d, color),

    mesh: (geometry, color, opts) => {
      // Freeform geos are per-build (not SHARED_GEOS). Own the material so
      // DoubleSide / dispose never touch the shared toonMat caches.
      // makeToon always stamps TOON_GRADIENT — override after for smooth roofs.
      // Textured planes (shop signs): MeshBasicMaterial — no lighting need, and
      // CanvasTexture works without fighting the toon ramp.
      if (opts?.map) {
        const mat = new THREE.MeshBasicMaterial({
          map: opts.map,
          color,
          transparent: opts.transparent ?? true,
          side: THREE.DoubleSide,
          // alphaTest + depthWrite keeps glyphs occluding cleanly against the board
          depthWrite: true,
          alphaTest: 0.05,
        });
        mat.userData.owned = true;
        mat.userData.ownedMaps = true; // disposeGroup frees map slots on owned mats
        opts.map.userData = { ...(opts.map.userData ?? {}), owned: true };
        const mesh = new THREE.Mesh(geometry, mat);
        mesh.userData.noConsolidate = true;
        push(mesh);
        return;
      }
      const mat = makeToon({
        color,
        side: opts?.smooth ? THREE.DoubleSide : THREE.FrontSide,
      });
      if (opts?.smooth) {
        mat.gradientMap = TOON_SMOOTH;
        mat.userData.smooth = true; // consolidate must preserve the soft ramp
      }
      mat.userData.owned = true;
      const mesh = new THREE.Mesh(geometry, mat);
      push(mesh);
    },

    noShadow: (fn) => {
      const prev = noShadowOn;
      noShadowOn = true;
      fn();
      noShadowOn = prev;
    },

    light: (x, y, z) => lights.push({ x, y, z }),
    dot: (x, z, color) => dots.push({ x, z, color }),
    seat: (x, y, z, face) => seats.push({ x, y, z, face }),
    door: (d) => {
      doors.push({
        hx: d.hx, hy: d.hy, hz: d.hz,
        x: d.cx, y: d.cy, z: d.cz,
        w: d.w, h: d.h,
        closedRy: d.closedRy, openDelta: d.openDelta,
        minX: d.minX, maxX: d.maxX, minZ: d.minZ, maxZ: d.maxZ,
        minY: d.minY, maxY: d.maxY,
        color: d.color,
      });
      // Closed door blocks the opening (doorId stamped later in world.ts).
      colliders.push({
        kind: "box",
        minX: d.minX, maxX: d.maxX, minZ: d.minZ, maxZ: d.maxZ,
        minY: d.minY, maxY: d.maxY,
        doorOpen: false,
      });
    },

    tagOp: (i) => { curOp = i; },

    layer: (name, fn) => {
      const prev = curLayer;
      curLayer = name;
      fn();
      curLayer = prev;
    },
  };

  spec.build(ctx);

  // Place everything: rotate by quarter-turns about Y, lift by baseY, then
  // translate to (originX, originZ). The mesh group does its own rotate+translate
  // via the THREE transform; the separately-stored physics/cut data is rotated
  // and offset here to match.
  group.rotation.y = turns * (Math.PI / 2);
  group.position.set(originX, baseY, originZ);

  for (const c of colliders) {
    if ((c as any).kind === "box") {
      const b = c as BoxCollider;
      const r = rotRect(b.minX, b.maxX, b.minZ, b.maxZ, turns);
      b.minX = r.minX + originX; b.maxX = r.maxX + originX;
      b.minZ = r.minZ + originZ; b.maxZ = r.maxZ + originZ;
      if (b.minY !== undefined) b.minY += baseY;
      if (b.maxY !== undefined) b.maxY += baseY;
    } else {
      const c2 = c as CircleCollider;
      const [x, z] = rotXZ(c2.x, c2.z, turns);
      c2.x = x + originX; c2.z = z + originZ;
      if (c2.minY !== undefined) c2.minY += baseY;
      if (c2.maxY !== undefined) c2.maxY += baseY;
    }
  }
  for (const s of surfaces) {
    const r = rotRect(s.minX, s.maxX, s.minZ, s.maxZ, turns);
    if (s.kind === "ramp") {
      // The low→high direction is along ±axis; rotate it to find the new axis +
      // which end (min/max) the low edge lands on.
      const loAlong = s.lowAt === "min";
      const [lx, lz] = s.axis === "x"
        ? [loAlong ? s.minX : s.maxX, (s.minZ + s.maxZ) / 2]
        : [(s.minX + s.maxX) / 2, loAlong ? s.minZ : s.maxZ];
      const [hx, hz] = s.axis === "x"
        ? [loAlong ? s.maxX : s.minX, (s.minZ + s.maxZ) / 2]
        : [(s.minX + s.maxX) / 2, loAlong ? s.maxZ : s.minZ];
      const [rlx, rlz] = rotXZ(lx, lz, turns);
      const [rhx, rhz] = rotXZ(hx, hz, turns);
      s.axis = Math.abs(rhx - rlx) >= Math.abs(rhz - rlz) ? "x" : "z";
      const loCoord = s.axis === "x" ? rlx + originX : rlz + originZ;
      const newMin = s.axis === "x" ? r.minX + originX : r.minZ + originZ;
      s.lowAt = Math.abs(loCoord - newMin) < 1e-3 ? "min" : "max";
      s.yLow += baseY; s.yHigh += baseY;
    } else {
      s.y += baseY;
    }
    s.minX = r.minX + originX; s.maxX = r.maxX + originX;
    s.minZ = r.minZ + originZ; s.maxZ = r.maxZ + originZ;
  }
  for (const l of lights) { const [x, z] = rotXZ(l.x, l.z, turns); l.x = x + originX; l.y += baseY; l.z = z + originZ; }
  for (const d of dots) { const [x, z] = rotXZ(d.x, d.z, turns); d.x = x + originX; d.z = z + originZ; }
  for (const st of seats) {
    const [x, z] = rotXZ(st.x, st.z, turns);
    st.x = x + originX; st.z = z + originZ; st.y += baseY;
    st.face += turns * (Math.PI / 2); // facing rotates with the frame (same convention as ry)
  }
  // Doors: hinge + center + closedRy rotate with the building. openDelta is a
  // signed yaw delta (invariant under quarter-turns). Door box colliders were
  // already rotated in the shared colliders loop — copy world AABB back onto
  // each door record (emit order matches colliders with doorOpen defined).
  {
    let di = 0;
    for (const c of colliders) {
      if ((c as BoxCollider).kind !== "box") continue;
      const b = c as BoxCollider;
      if (b.doorOpen === undefined) continue;
      if (di >= doors.length) break;
      const d = doors[di++]!;
      d.minX = b.minX; d.maxX = b.maxX; d.minZ = b.minZ; d.maxZ = b.maxZ;
      d.minY = b.minY ?? 0; d.maxY = b.maxY ?? d.h;
    }
  }
  for (const d of doors) {
    const [hx, hz] = rotXZ(d.hx, d.hz, turns);
    const [cx, cz] = rotXZ(d.x, d.z, turns);
    d.hx = hx + originX; d.hz = hz + originZ; d.hy += baseY;
    d.x = cx + originX; d.z = cz + originZ; d.y += baseY;
    d.closedRy += turns * (Math.PI / 2);
  }
  for (const m of cutaway) {
    const cut = m.userData.cut as CutTag;
    const [cx, cz] = rotXZ(cut.cx, cut.cz, turns);
    cut.cx = cx + originX; cut.cz = cz + originZ;
    const [nx, nz] = rotXZ(cut.nx, cut.nz, turns); // normals rotate as directions
    cut.nx = nx; cut.nz = nz;
    if (cut.nx2 !== undefined && cut.nz2 !== undefined) {
      const [nx2, nz2] = rotXZ(cut.nx2, cut.nz2, turns);
      cut.nx2 = nx2; cut.nz2 = nz2;
    }
    cut.y0 += baseY; cut.y1 += baseY;
  }
  if (ceilingY !== undefined) ceilingY += baseY;

  // Derive the interior (footprint + floor heights) from the walkable slabs, now
  // in world coords. A structure with no platforms isn't enterable (floors: []).
  // PROP platforms (furniture/wall tops, gravity plan) are standable but never
  // storeys — skipping them here keeps cutaway + shell-splitting untouched.
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const floorSet = new Set<number>();
  for (const s of surfaces) {
    if (s.kind !== "platform" || s.prop) continue;
    minX = Math.min(minX, s.minX); maxX = Math.max(maxX, s.maxX);
    minZ = Math.min(minZ, s.minZ); maxZ = Math.max(maxZ, s.maxZ);
    floorSet.add(Math.round(s.y * 100) / 100);
  }
  let top = 0;
  for (const c of colliders) top = Math.max(top, (c as any).maxY ?? 0);
  for (const m of cutaway) top = Math.max(top, (m.userData.cut as CutTag).y1);
  const interior: InteriorInfo = floorSet.size
    ? { minX, maxX, minZ, maxZ, floors: [...floorSet].sort((a, b) => a - b), top, roofCeiling: ceilingY }
    : { minX: 0, maxX: 0, minZ: 0, maxZ: 0, floors: [], top: 0 };

  // Collapse the mesh-per-primitive build into one draw per fade class. Runs
  // AFTER the tags are in world coords and the interior's floor lines are known
  // (the fade-equivalence keying needs both). Rewrites `cutaway` in place.
  // The studio Builder opts out to keep per-primitive meshes + their op tags
  // (still storey-splits so the editable preview matches game cutaway bands).
  // privateMats when Builder opts out of consolidate — each storey band needs
  // its own fade material (shared toonMat would thrash opacity across bands).
  if (interior.floors.length > 1) {
    splitShellsByFloors(group, cutaway, interior, opts?.consolidate === false);
  }
  if (opts?.consolidate !== false) consolidate(group, cutaway, interior);

  return { group, colliders, surfaces, lights, dots, seats, doors, interior, cutaway };
}
